/**
 * Comprehensive SQL Autocomplete Test Script
 * Run this in the browser console to test the autocomplete functionality
 */

// Extend window type for Monaco editor testing
declare global {
  interface Window {
    monacoEditor?: {
      getValue(): string;
      getPosition(): { lineNumber: number; column: number };
      setValue(value: string): void;
      setPosition(position: { lineNumber: number; column: number }): void;
      trigger(source: string, action: string, payload?: unknown): void;
      getModel(): unknown;
    };
    monaco?: {
      languages: {
        getLanguages(): unknown[];
        getCompletionProviders(languageId: string): unknown;
      };
    };
    runAutocompleteTests?: typeof runAutocompleteTests;
    testColumnFetch?: typeof testColumnFetch;
    testCompletionProvider?: typeof testCompletionProvider;
  }
}

export function runAutocompleteTests() {
  console.log('🧪 Starting SQL Autocomplete Tests...');

  const editor = window.monacoEditor;
  if (!editor) {
    console.error('❌ Monaco editor not found. Please open the SQL editor first.');
    return;
  }

  // Test scenarios
  const testScenarios = [
    {
      name: 'Basic SELECT with table name',
      sql: 'SELECT * FROM users WHERE ',
      expectedTables: ['users'],
      description: 'Should suggest column names from users table'
    },
    {
      name: 'Table dot notation',
      sql: 'SELECT users.',
      expectedTables: ['users'],
      description: 'Should suggest columns from users table after dot'
    },
    {
      name: 'JOIN query',
      sql: 'SELECT u.name, p.title FROM users u JOIN posts p ON u.id = p.user_id WHERE u.',
      expectedTables: ['users', 'posts'],
      description: 'Should suggest columns from users table (alias u)'
    },
    {
      name: 'Multiple tables',
      sql: 'SELECT * FROM users u JOIN orders o ON u.id = o.user_id WHERE o.',
      expectedTables: ['users', 'orders'],
      description: 'Should suggest columns from orders table (alias o)'
    },
    {
      name: 'UPDATE statement',
      sql: 'UPDATE users SET name = \'John\' WHERE ',
      expectedTables: ['users'],
      description: 'Should suggest columns from users table in WHERE clause'
    }
  ];

  let passedTests = 0;
  const totalTests = testScenarios.length;

  // Run each test scenario
  for (const [index, scenario] of testScenarios.entries()) {
    console.log(`\n🔍 Test ${index + 1}: ${scenario.name}`);
    console.log(`📝 SQL: ${scenario.sql}`);
    console.log(`📋 Description: ${scenario.description}`);
    
    try {
      // Set the SQL in the editor
      editor.setValue(scenario.sql);
      editor.setPosition({ lineNumber: 1, column: scenario.sql.length + 1 });
      
      // Test table extraction
      const extractedTables = testTableExtraction(scenario.sql);
      console.log(`📊 Extracted tables: ${extractedTables.join(', ')}`);
      
      // Check if expected tables are found
      const foundAllTables = scenario.expectedTables.every(table => 
        extractedTables.some(extracted => extracted.toLowerCase() === table.toLowerCase())
      );
      
      if (foundAllTables) {
        console.log('✅ Table extraction: PASSED');
        passedTests++;
      } else {
        console.log('❌ Table extraction: FAILED');
        console.log(`   Expected: ${scenario.expectedTables.join(', ')}`);
        console.log(`   Found: ${extractedTables.join(', ')}`);
      }
      
      // Trigger autocomplete and wait for results
      setTimeout(() => {
        editor.trigger('keyboard', 'editor.action.triggerSuggest', {});
        console.log('🎯 Autocomplete triggered');
      }, 100);
      
    } catch (error) {
      console.error(`❌ Test ${index + 1} failed:`, error);
    }
  }

  console.log(`\n📈 Test Results: ${passedTests}/${totalTests} tests passed`);
  
  if (passedTests === totalTests) {
    console.log('🎉 All tests passed!');
  } else {
    console.log('⚠️  Some tests failed. Check the logs above for details.');
  }
}

function testTableExtraction(sql: string): string[] {
  const patterns = [
    // FROM table [alias]
    /FROM\s+(?:(\w+)\.)?(\w+)(?:\s+(?:AS\s+)?(\w+))?/gi,
    // JOIN table [alias]
    /(?:INNER\s+|LEFT\s+|RIGHT\s+|FULL\s+|CROSS\s+)?JOIN\s+(?:(\w+)\.)?(\w+)(?:\s+(?:AS\s+)?(\w+))?/gi,
    // UPDATE table
    /UPDATE\s+(?:(\w+)\.)?(\w+)/gi,
    // INSERT INTO table
    /INSERT\s+INTO\s+(?:(\w+)\.)?(\w+)/gi,
    // DELETE FROM table
    /DELETE\s+FROM\s+(?:(\w+)\.)?(\w+)/gi
  ];
  
  const tableNames: string[] = [];
  for (const pattern of patterns) {
    let match;
    pattern.lastIndex = 0;
    while ((match = pattern.exec(sql)) !== null) {
      const tableName = match[2] || match[1];
      if (tableName && !tableNames.includes(tableName)) {
        tableNames.push(tableName);
      }
    }
  }

  return tableNames;
}

// Manual column fetch test
export async function testColumnFetch(tableName: string) {
  console.log(`🔍 Testing column fetch for table: ${tableName}`);
  
  try {
    // Get datasource info from the current page context
    const urlParams = new URLSearchParams(window.location.search);
    const datasourceId = urlParams.get('datasourceId') || 'test-datasource';
    const databaseName = urlParams.get('databaseName') || 'test-database';
    
    console.log(`📡 Using datasourceId: ${datasourceId}, databaseName: ${databaseName}`);
    
    const { DatabaseOperationsService } = await import('@/services/api/services/DatabaseOperationsService');
    
    const response = await DatabaseOperationsService.agentProxyCommand({
      datasourceId,
      requestBody: {
        type: 'get_column_info',
        timeout: 30,
        data: {
          database_name: databaseName,
          table_name: tableName,
          nonce: crypto.randomUUID(),
          timestamp: Math.floor(Date.now() / 1000)
        }
      }
    });

    console.log(`✅ Successfully fetched columns for ${tableName}:`, response);
    
    // Test column structure
    const columns = ('columns' in response && Array.isArray(response.columns)) ? response.columns : [];
    if (Array.isArray(columns)) {
      for (const [index, column] of columns.entries()) {
        const colObj = (typeof column === 'object' && column !== null) ? (column as Record<string, unknown>) : undefined
        console.log(`   Column ${index + 1}:`, {
          name: typeof column === 'string' ? column : (colObj && typeof colObj.column_name === 'string' ? colObj.column_name : undefined),
          type: colObj && typeof colObj.data_type === 'string' ? colObj.data_type : 'unknown',
          nullable: colObj && typeof colObj.is_nullable === 'boolean' ? colObj.is_nullable : undefined,
          isPrimaryKey: colObj && typeof colObj.is_primary_key === 'boolean' ? colObj.is_primary_key : undefined
        });
      }
    }
    
    return response;
  } catch (error) {
    console.error(`❌ Error fetching columns for ${tableName}:`, error);
    
    // Log more details about the error
    if (error && typeof error === 'object') {
      const errObj = error as Record<string, unknown>
      console.error('Error details:', {
        message: typeof errObj.message === 'string' ? errObj.message : undefined,
        status: typeof errObj.status === 'number' ? errObj.status : undefined,
        body: 'body' in errObj ? errObj.body : undefined
      });
    }
    
    throw error;
  }
}

// Test the completion provider directly
export function testCompletionProvider() {
  console.log('🧪 Testing completion provider...');

  const editor = window.monacoEditor;
  if (!editor) {
    console.error('❌ Monaco editor not found.');
    return;
  }

  const model = editor.getModel();
  if (!model) {
    console.error('❌ No model found.');
    return;
  }

  // Test SQL
  const testSql = 'SELECT * FROM users WHERE ';
  editor.setValue(testSql);
  
  const position = { lineNumber: 1, column: testSql.length + 1 };
  editor.setPosition(position);
  
  console.log(`📝 Test SQL: ${testSql}`);
  console.log(`📍 Position: line ${position.lineNumber}, column ${position.column}`);
  
  // Try to get suggestions manually
  const { monaco } = window;
  if (monaco) {
    console.log('🎯 Triggering completion provider...');
    
    // Get registered providers
    try {
      monaco.languages.getCompletionProviders('sql');
      console.log('✅ SQL completion providers are registered');
    } catch (error) {
      console.error('❌ Error getting completion providers:', error);
    }
  }
}

// Make functions available globally
if (typeof window !== 'undefined') {
  window.runAutocompleteTests = runAutocompleteTests;
  window.testColumnFetch = testColumnFetch;
  window.testCompletionProvider = testCompletionProvider;
}
